import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TmCartLine } from '@/tscapi/model/cart';
import { TmProduct } from '@/tscapi/model/product';

import { en, fr } from '../i18n';

// Preload translations outside the component to avoid redundant execution
const preloadTranslations = (i18n) => {
    if (!i18n.hasResourceBundle('en', 'addtobag')) {
        i18n.addResourceBundle('en', 'addtobag', en, true, true);
    }
    if (!i18n.hasResourceBundle('fr', 'addtobag')) {
        i18n.addResourceBundle('fr', 'addtobag', fr, true, true);
    }
};

type ItemCardProps = {
    CartLine: TmCartLine;
    CartProducts: TmProduct[];
    Remove: () => void;
    Update: () => void;
    QuantityAddedOveride?: number;
};

export const ItemCard = ({ CartLine, CartProducts, Remove, QuantityAddedOveride }: ItemCardProps) => {
    const { i18n, t } = useTranslation('addtobag');
    preloadTranslations(i18n);

    const product = CartProducts.find((x) => x.ItemNo === CartLine.CartLineItem.ItemNo);
    const edpData = product?.Edps.find((y) => y.EdpNo === CartLine.CartLineItem.EdpNo);
    const message = CartLine?.InCart?.UpsellMessage?.trim();
    const isUpsellUnlocked = Boolean(message && message.toLowerCase().startsWith('deal'));
    if (!product) {
        return null;
    }

    return (
        <>
            {CartLine?.InCart?.UpsellMessage && (
                <span
                    style={{ borderColor: isUpsellUnlocked ? '#2a979d' : '#ff0000' }}
                    className="mb-2.5 inline-flex items-center border-2 border-solid px-2 py-1"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 15 15"
                        width="15"
                        height="15"
                        className="mr-1.5 inline-block"
                        fill={isUpsellUnlocked ? '#2a979d' : '#ff0000'}
                    >
                        <path d="M14.52 8.27 7.55 1.3a.543.543 0 0 0-.34-.16L1.73.64C1.11.58.59 1.1.64 1.73l.5 5.48c.01.13.07.25.16.34l6.97 6.97c.22.22.57.22.78 0l5.47-5.47c.22-.22.22-.57 0-.78ZM3.81 3.81c-.44.44-1.15.44-1.59 0-.44-.44-.44-1.15 0-1.59.44-.44 1.15-.44 1.59 0s.44 1.15 0 1.59Z" />
                    </svg>

                    <span className="text-xsm font-semibold leading-4.5">
                        <span className={!isUpsellUnlocked ? 'pr-2' : ''}>{CartLine.InCart.UpsellMessage} </span>
                        {!isUpsellUnlocked && (
                            <a
                                href="/pages/purchasewithpurchase"
                                className="whitespace-nowrap text-xsm underline hover:font-semibold hover:underline"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Learn More
                            </a>
                        )}
                    </span>
                </span>
            )}
            <div className="grid grid-cols-[96px_auto_1px] gap-x-4 pb-3.5 md:grid-cols-[96px_auto_85px]">
                <div className="z col-span-1 w-24">
                    <a href={product?.ProductLink.LinkWithoutDomain} target="_blank" rel="noreferrer">
                        <img
                            alt=""
                            id={`badge${CartLine.CartLineItem.ItemNo}`}
                            className="img-responsive center-block"
                            width="96"
                            height="96"
                            src={`https://item.tscimg.ca/TSC/${CartLine.CartLineItem.ImageUrl}?impolicy=M`}
                        />
                    </a>
                </div>
                <div className="col-start-2 col-end-4 text-[13px] font-light md:col-end-2">
                    {product.PriceIsLabel && (
                        <div className="mb-2 text-[10px] font-semibold uppercase leading-normal text-black">
                            {product.PriceIsLabel}
                        </div>
                    )}

                    <a href={product.ProductLink.LinkWithoutDomain} target="_blank" rel="noreferrer">
                        <span className="mb-2 line-clamp-2 text-[13px] font-semibold leading-tight">
                            {product.Name}
                        </span>
                    </a>

                    <div>
                        {CartLine.CartLineItem.ItemNo.slice(0, 3) + '-' + CartLine.CartLineItem.ItemNo.slice(3, 6)}
                    </div>

                    {(CartLine.CartLineItem.Size || CartLine.CartLineItem.Style) && (
                        <div>
                            {CartLine.CartLineItem.Size && (
                                <span>
                                    {t('Size')}: {CartLine.CartLineItem.Size}
                                </span>
                            )}
                            {CartLine.CartLineItem.Size && CartLine.CartLineItem.Style && (
                                <span className="px-1.5">|</span>
                            )}
                            {CartLine.CartLineItem.Style && (
                                <span>
                                    {t('Style')}: {CartLine.CartLineItem.Style}
                                </span>
                            )}
                        </div>
                    )}

                    <div>Quantity: {QuantityAddedOveride ? QuantityAddedOveride : CartLine.InCart.Quantity}</div>
                </div>
                <div className="col-start-2 col-end-4 row-start-2 row-end-3 mb-2 text-right text-sm md:col-start-3 md:col-end-4 md:row-start-1 md:row-end-2">
                    <div className="flex-col md:flex md:flex-wrap">
                        <span className="font-semibold">{CartLine.InCart.UnitPrice}</span>
                        {CartLine.InCart.UnitPrice !== edpData?.WasPrice && (
                            <span className="ml-2.5 line-through md:ml-0">{edpData?.WasPrice}</span>
                        )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        {!CartLine.CartLineItem.IsDonation &&
                            (CartLine.InCart.ShippingHandling === 'FREE' ||
                                CartLine.InCart.ShippingHandling === 'GRATUIT') && (
                                <span className="mt-2.5 inline-block border border-solid border-black px-1.5 text-xxsm font-semibold">
                                    {t('Free shipping')}
                                </span>
                            )}
                    </div>
                </div>
                {CartLine.InCart.IsEditable && (
                    <div className="col-start-2 col-end-4 row-start-3 row-end-4 text-right text-sm">
                        <button type="button" className="text-[13px] underline" onClick={Remove}>
                            {t('Remove')}
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};
